import * as React from 'react';
import { i18n } from '@lingui/core';
import { Outlet, useParams, useRevalidator } from 'react-router';
import DealerSelectionDialog from 'components/DealerSelectionDialog';
import { EErrorCode } from 'config/errors-codes';
import { brandSchema, marketSchema } from 'data/schema';
import type { IBrand, IMarket } from 'data/schema';
import { useError } from 'hooks/useError';
import { isAuthenticated } from 'utils/auth';
import { takeDealerIdFromUrl } from 'utils/auth/applyDealerIdFromUrl';
import {
  activateDealerTenant,
  isDealerGatePassed,
  markDealerGatePassed,
  readSessionDealerHint,
} from 'utils/auth/dealerGate';
import { getEligibleDealerIds, getNoMatchingDealerMessage, resolveDealerForLogin } from 'utils/auth/dealerResolution';
import type { IDealerSelectionError } from 'utils/auth/dealerResolution.types';
import { commitDealerSelection } from 'utils/auth/dealerSelection';
import { user } from 'utils/auth/user';
import { sendApiRequest } from 'utils/network';
import type { ICustomerFacingErrorOptions } from 'utils/ui/errors';

interface IDealerAccessResponse {
  allowed: boolean;
  code?: EErrorCode;
}

/**
 * Validates dealer access against the backend whitelist.
 * Only runs for dealer users when whitelisting might be enabled.
 */
async function validateDealerAccess(
  brand: string,
  market: string,
  dealerId: string
): Promise<{ allowed: boolean; code?: EErrorCode }> {
  const response = await sendApiRequest<IDealerAccessResponse>(`/dealer-access?brand=${brand}&market=${market}`, {
    headers: {
      'X-Dealer': dealerId,
    },
  });

  return response ?? { allowed: true };
}

/** What SecureOutlet renders: nothing while the gate works, the dealer dialog, or the routed children. */
type TGateView =
  | { status: 'pending' }
  | { status: 'open' }
  | {
      status: 'choose';
      brand: IBrand;
      market: IMarket;
      options: readonly string[];
      error?: IDealerSelectionError;
      onSelect: (dealerId: string) => void;
    };

const PENDING: TGateView = { status: 'pending' };
const OPEN: TGateView = { status: 'open' };

/** One run of the gate for one brand and market. Every effect of a run goes through it, so a stale run is inert. */
interface IGateRun {
  brand: IBrand;
  market: IMarket;
  sessionId?: string;
  dealerIds: readonly string[];
  isCancelled: () => boolean;
  show: (view: TGateView) => void;
  fail: (code: EErrorCode, options?: ICustomerFacingErrorOptions) => void;
  revalidate: () => Promise<unknown>;
}

/** Any unexpected failure ends in the same error screen the gate always used for it. */
async function guarded(run: IGateRun, step: () => Promise<void>): Promise<void> {
  try {
    await step();
  } catch {
    run.fail(EErrorCode.DISABLED_MARKET);
  }
}

/**
 * Tenant config of THIS brand and market, then the market check, the whitelist, and finally the
 * deferred route loaders: nothing renders before the loaders have re-run with the gate open.
 */
async function openGate(run: IGateRun, dealerId: string): Promise<void> {
  const { brand, market } = run;
  // Read before anything is marked: on a remount for the same brand, market and dealer the route loaders already
  // ran with the gate open, so re-running them (initSession, session:resume) would only repeat their side effects.
  const loadersAlreadyRan = isDealerGatePassed(brand, market);

  const inactive = await activateDealerTenant(brand, market);
  if (run.isCancelled()) {
    return;
  }

  if (inactive) {
    run.fail(EErrorCode.DISABLED_MARKET);
    return;
  }

  if (dealerId) {
    const access = await validateDealerAccess(brand, market, dealerId);
    if (run.isCancelled()) {
      return;
    }

    if (!access.allowed) {
      run.fail(access.code ?? EErrorCode.DEALER_NOT_WHITELISTED);
      return;
    }
  }

  markDealerGatePassed(brand, market, dealerId);
  if (!loadersAlreadyRan) {
    await run.revalidate();
  }
  run.show(OPEN);
}

function showDialog(run: IGateRun, options: readonly string[], error?: IDealerSelectionError): void {
  run.show({
    status: 'choose',
    brand: run.brand,
    market: run.market,
    options,
    error,
    onSelect: (dealerId) => {
      void guarded(run, () => commitAndOpen(run, dealerId));
    },
  });
}

/** Stores a valid choice for this login and opens the gate; a rejected one brings the dialog back with the reason. */
async function commitAndOpen(run: IGateRun, dealerId: string): Promise<void> {
  const { brand, market, dealerIds } = run;

  const committed = commitDealerSelection(dealerId, { dealerIds, brand, market });
  if (!committed.ok) {
    showDialog(run, getEligibleDealerIds(dealerIds, brand, market), committed.error);
    return;
  }

  run.show(PENDING);
  await openGate(run, user.selectedDealerId);
}

async function resolveDealer(run: IGateRun): Promise<void> {
  const { brand, market, sessionId, dealerIds } = run;

  const urlDealerId = takeDealerIdFromUrl();
  const storedDealerId = user.selectedDealerId || undefined;
  // The session's own dealer is only a hint, so it is read only when nothing more specific was given.
  const needsHint = !urlDealerId && !storedDealerId && sessionId;
  const sessionDealerId = needsHint ? await readSessionDealerHint(sessionId) : undefined;
  if (run.isCancelled()) {
    return;
  }

  const resolution = resolveDealerForLogin({ dealerIds, brand, market, urlDealerId, storedDealerId, sessionDealerId });

  if (resolution.status === 'none') {
    run.fail(EErrorCode.DEALER_NO_MATCHING_BRAND_MARKET, {
      message: i18n._(getNoMatchingDealerMessage(brand, market)),
    });
    return;
  }

  if (resolution.status === 'choose') {
    showDialog(run, resolution.options, resolution.error);
    return;
  }

  await commitAndOpen(run, resolution.dealerId);
}

/**
 * SecureOutlet is a wrapper around the react-router Outlet component.
 * It is the standalone dealer gate (AVS-4405): until the dealer is chosen and this brand and market's tenant config
 * allows access, it renders no routes, and no session or tenant data is loaded.
 */
export const SecureOutlet = () => {
  const throwError = useError();
  const { revalidate } = useRevalidator();
  const { brand, market, sessionId } = useParams();
  const [view, setView] = React.useState<TGateView>(PENDING);

  // `useError()` returns a new function on every render, so the effect reads the latest through a ref
  // and re-runs only for a new brand or market.
  const latest = React.useRef({ throwError, revalidate, sessionId });
  latest.current = { throwError, revalidate, sessionId };

  React.useEffect(() => {
    let cancelled = false;
    setView(PENDING);

    // If the user is not authenticated, the dealer gate does not apply yet: let the user complete the
    // authentication flow (ProtectedRoute redirects), without waiting for any tenant config.
    if (!isAuthenticated()) {
      setView(OPEN);
      return;
    }

    // The route loader already rejects an unknown brand or market; this keeps the types honest without a cast.
    const parsedBrand = brandSchema.safeParse(brand);
    if (!parsedBrand.success) {
      latest.current.throwError(EErrorCode.INVALID_BRAND);
      return;
    }

    const parsedMarket = marketSchema.safeParse(market);
    if (!parsedMarket.success) {
      latest.current.throwError(EErrorCode.INVALID_MARKET);
      return;
    }

    const run: IGateRun = {
      brand: parsedBrand.data,
      market: parsedMarket.data,
      sessionId: latest.current.sessionId,
      dealerIds: user.isDealer ? user.dealerIdsList : [],
      isCancelled: () => cancelled,
      show: (next) => {
        if (!cancelled) {
          setView(next);
        }
      },
      fail: (code, options) => {
        if (!cancelled) {
          latest.current.throwError(code, options);
        }
      },
      revalidate: () => latest.current.revalidate(),
    };

    void guarded(run, () => (user.isDealer ? resolveDealer(run) : openGate(run, '')));

    return () => {
      cancelled = true;
    };
  }, [brand, market]);

  if (view.status === 'pending') {
    return null;
  }

  if (view.status === 'choose') {
    return (
      <DealerSelectionDialog
        options={view.options}
        brand={view.brand}
        market={view.market}
        error={view.error}
        onSelect={view.onSelect}
      />
    );
  }

  return <Outlet />;
};
